import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';
import type { ComponentProps, ReactNode } from 'react';
import React, { useState } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ConfluenceLogo } from '@atlaskit/logo';
import { N300, N30A } from '@atlaskit/theme/colors';
import Tooltip from '@atlaskit/tooltip';
import { ANALYTICS_CHANNEL } from '../analytics';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	fontSize: 12,
	height: 52,
	color: token('color.text.subtle', N300),
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	borderTop: `2px solid ${token('color.border', N30A)}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	height: 16,
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'flex-end',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LogoWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	margin: `0px 0px 0px ${token('space.100', '8px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Link = styled.a({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&, &:focus, &:active, &:hover': {
		color: 'inherit',
		outline: 'none',
		boxShadow: 'none',
		textDecoration: 'none',
	},
});

export type FooterProps = Readonly<{
	url: string | null;
	linkTooltip: ReactNode;
	label: ReactNode;
}>;

export const Footer = ({ url, linkTooltip, label }: FooterProps) => (
	<Container data-testid="footer-container">
		{url ? (
			<ContentAction tooltipText={linkTooltip} href={url}>
				{({ isHovered }) => <Content isHovered={isHovered} label={label} />}
			</ContentAction>
		) : (
			<Content label={label} />
		)}
	</Container>
);

type ContentProps = Readonly<{
	isHovered?: boolean;
	label: ReactNode;
}>;

const Content = ({ isHovered, label }: ContentProps) => (
	<ContentWrapper>
		{label}
		<LogoWrapper>
			<ConfluenceLogo appearance={isHovered ? 'brand' : 'neutral'} size="xsmall" />
		</LogoWrapper>
	</ContentWrapper>
);

type ContentActionProps = {
	tooltipText: ComponentProps<typeof Tooltip>['content'];
	href: string;
	children: ({ isHovered }: { isHovered: boolean }) => ReactNode;
};

const ContentAction = ({ children, tooltipText, href }: ContentActionProps) => {
	const [isHovered, setIsHovered] = useState<boolean>(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleClick = () => {
		createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'confluenceFooter',
			source: 'embeddedConfluenceViewPageScreen',
			eventType: 'ui',
		}).fire(ANALYTICS_CHANNEL);
	};

	return (
		<Tooltip content={tooltipText}>
			<Link
				href={href}
				target="_blank"
				onMouseEnter={() => {
					setIsHovered(true);
				}}
				onMouseLeave={() => {
					setIsHovered(false);
				}}
				onClick={handleClick}
				data-testid="link"
			>
				{children({ isHovered })}
			</Link>
		</Tooltip>
	);
};
